import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import Button from './Button';
import QuotePopup from './QuotePopup';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Navigation items - About is second
  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/contact', label: 'Contact' },
  ];

  // Scroll progress
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const menuVariants = {
    closed: {
      opacity: 0,
      x: "100%",
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    },
    open: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };

  const menuItemVariants = {
    closed: { opacity: 0, x: 20 },
    open: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.3
      }
    })
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/90 backdrop-blur-md shadow-lg py-3">
      {/* Scroll Progress Bar - only show when scrolled */}
      {isScrolled && (
        <motion.div
          className="absolute top-0 left-0 right-0 h-1 bg-secondary origin-left"
          style={{ scaleX }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        />
      )}

      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between">
          {/* Logo - Left Corner */}
          <div className="flex items-center">
            <Link to="/">
              <img
                src="/Assets/Logo/logo.png"
                alt="Alpha Laser Cutting Logo"
                className="h-8 md:h-10 w-auto cursor-pointer hover:scale-105 transition-transform duration-300"
              />
            </Link>
          </div>

          {/* Desktop Navigation - Center */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path !== '/' && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-medium transition-all duration-300 relative ${
                    isActive 
                      ? 'text-secondary' 
                      : 'text-steel hover:text-secondary'
                  }`}
                >
                  {item.label}
                  {isActive && (
                    <motion.div
                      className="absolute -bottom-1 left-0 right-0 h-0.5 bg-secondary"
                      layoutId="activeNav"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Get Quote Button */}
          <div className="hidden md:flex items-center flex-shrink-0">
            <motion.button
              onClick={() => setIsQuoteOpen(true)}
              className="bg-secondary hover:bg-red-600 text-white px-6 py-2.5 rounded-full font-semibold text-sm flex items-center gap-2 transition-all duration-300 shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Quote
              <ArrowUpRight className="w-4 h-4" />
            </motion.button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={toggleMenu}
              className="text-steel hover:text-secondary transition-colors duration-200 p-2"
              aria-label="Toggle menu"
            >
              <motion.div
                animate={isMenuOpen ? "open" : "closed"}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {isMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </motion.div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-steel/60 backdrop-blur-sm md:hidden"
              style={{ top: '80px' }}
              onClick={closeMenu}
            />

            {/* Sliding Menu */}
            <motion.div
              variants={menuVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="fixed right-0 top-20 h-[calc(100vh-80px)] w-[280px] bg-white shadow-2xl md:hidden overflow-y-auto"
            >
              <nav className="flex flex-col p-6 space-y-6">
                {/* Navigation Links */}
                <div className="space-y-4">
                  <motion.div
                    custom={0}
                    variants={menuItemVariants}
                    initial="closed"
                    animate="open"
                  >
                    <Link
                      to="/"
                      onClick={closeMenu}
                      className="block text-steel hover:text-primary transition-colors duration-200 font-semibold font-poppins text-lg py-2 border-b border-gray-100 hover:border-primary"
                    >
                      Home
                    </Link>
                  </motion.div>

                  <motion.div
                    custom={1}
                    variants={menuItemVariants}
                    initial="closed"
                    animate="open"
                  >
                    <Link
                      to="/about"
                      onClick={closeMenu}
                      className="block text-steel hover:text-primary transition-colors duration-200 font-semibold font-poppins text-lg py-2 border-b border-gray-100 hover:border-primary"
                    >
                      About
                    </Link>
                  </motion.div>

                  <motion.div
                    custom={2}
                    variants={menuItemVariants}
                    initial="closed"
                    animate="open"
                  >
                    <Link
                      to="/services"
                      onClick={closeMenu}
                      className="block text-steel hover:text-primary transition-colors duration-200 font-semibold font-poppins text-lg py-2 border-b border-gray-100 hover:border-primary"
                    >
                      Services
                    </Link>
                  </motion.div>

                  <motion.div
                    custom={3}
                    variants={menuItemVariants}
                    initial="closed"
                    animate="open"
                  >
                    <Link
                      to="/contact"
                      onClick={closeMenu}
                      className="block text-steel hover:text-primary transition-colors duration-200 font-semibold font-poppins text-lg py-2 border-b border-gray-100 hover:border-primary"
                    >
                      Contact
                    </Link>
                  </motion.div>
                </div>

                {/* Mobile Buttons */}
                <motion.div
                  custom={5}
                  variants={menuItemVariants}
                  initial="closed"
                  animate="open"
                  className="space-y-3 pt-4"
                >
                  <Link to="/contact" onClick={closeMenu}>
                    <Button variant="outline" size="md" className="w-full">
                      Contact Us
                    </Button>
                  </Link>
                  <Button 
                    variant="secondary" 
                    size="md" 
                    className="w-full"
                    onClick={() => {
                      setIsQuoteOpen(true);
                      closeMenu();
                    }}
                  >
                    Get Quote
                  </Button>
                </motion.div>

                {/* Decorative Element */}
                <motion.div
                  custom={6}
                  variants={menuItemVariants}
                  initial="closed"
                  animate="open"
                  className="pt-6 mt-6 border-t border-gray-200"
                >
                  <div className="text-center">
                    <p className="text-sm text-steel-lighter">Alpha Laser Cutting</p>
                    <p className="text-xs text-steel-lighter mt-1">Precision Engineering</p>
                  </div>
                </motion.div>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Quote Popup */}
      <QuotePopup isOpen={isQuoteOpen} onClose={() => setIsQuoteOpen(false)} />
    </header>
  );
};

export default Header;